/*
 * Разбор объектов операторов хранилищ из Kubernetes API кластера для страниц PostgreSQL
 * (CloudNativePG: Cluster, Database, DatabaseRole, поды инстансов) и Kafka (Strimzi: Kafka,
 * KafkaNodePool, KafkaTopic, поды узлов). Базы и пользователи PostgreSQL заводятся только
 * через IaC (values.yaml кластера в gitops → чарт postgres-cluster → CR оператора), поэтому
 * их списки — это CR, а не состояние внутри PostgreSQL. Чистые функции без React —
 * покрыты юнит-тестами.
 */

export const DATASTORES_NAMESPACE = 'datastores';

type Condition = { type: string; status: string; message?: string };

/* ----------------------------- PostgreSQL (CNPG) ----------------------------- */

export type CnpgCluster = {
  metadata: { name: string };
  spec?: {
    instances?: number;
    imageName?: string;
    enableSuperuserAccess?: boolean;
    storage?: { size?: string };
    resources?: {
      requests?: Record<string, string>;
      limits?: Record<string, string>;
    };
    bootstrap?: { initdb?: { database?: string; owner?: string } };
    managed?: {
      roles?: {
        name: string;
        ensure?: string;
        login?: boolean;
        superuser?: boolean;
        createdb?: boolean;
        createrole?: boolean;
        replication?: boolean;
        inherit?: boolean;
        connectionLimit?: number;
        passwordSecret?: { name: string };
      }[];
    };
  };
  status?: {
    phase?: string;
    readyInstances?: number;
    instances?: number;
    currentPrimary?: string;
    targetPrimary?: string;
    instancesStatus?: Record<string, string[]>;
    managedRolesStatus?: Record<string, string[]>;
    image?: string;
    conditions?: Condition[];
  };
};

export type CnpgDatabase = {
  metadata: { name: string };
  spec?: {
    name?: string;
    owner?: string;
    cluster?: { name?: string };
    ensure?: string;
    extensions?: { name: string; ensure?: string }[];
  };
  status?: { applied?: boolean; message?: string };
};

/** DatabaseRole CloudNativePG: роль (пользователь) кластера, описанная декларативно. */
export type CnpgDatabaseRole = {
  metadata: { name: string };
  spec?: {
    name?: string;
    cluster?: { name?: string };
    ensure?: string;
    login?: boolean;
    superuser?: boolean;
    createdb?: boolean;
    createrole?: boolean;
    replication?: boolean;
    inherit?: boolean;
    connectionLimit?: number;
    comment?: string;
    inRoles?: string[];
    passwordSecret?: { name: string };
  };
  status?: { applied?: boolean; message?: string };
};

export type DatastorePod = {
  metadata: { name: string; labels?: Record<string, string> };
  spec?: { nodeName?: string };
  status?: {
    phase?: string;
    podIP?: string;
    containerStatuses?: { ready?: boolean; restartCount?: number }[];
  };
};

export type PgInstance = {
  name: string;
  role: 'primary' | 'replica' | 'unknown';
  /** healthy / replicating / failed / … из status.instancesStatus. */
  health: string;
  node?: string;
  podIp?: string;
  phase?: string;
  ready: boolean;
  restarts: number;
};

export type PgUser = {
  name: string;
  /**
   * Откуда известен: владелец первой базы (initdb), суперпользователь postgres,
   * CR DatabaseRole (users в values.yaml) или spec.managed.roles кластера.
   */
  source: 'owner' | 'superuser' | 'role' | 'managed';
  /** Имя CR DatabaseRole. */
  resource?: string;
  login: boolean;
  superuser: boolean;
  createdb: boolean;
  comment?: string;
  /** Секрет с паролем в namespace datastores (имя, не содержимое). */
  secret?: string;
  /** DatabaseRole: применена ли оператором; undefined — ещё нет статуса. */
  applied?: boolean;
  /** Сообщение оператора (обычно ошибка применения). */
  message?: string;
  /** Состояние управляемой роли из status.managedRolesStatus. */
  status?: string;
};

export type PgDatabase = {
  name: string;
  owner?: string;
  /** Откуда известна: initdb кластера (первая база) или CR Database (databases в values.yaml). */
  source: 'bootstrap' | 'database';
  /** Имя CR Database. */
  resource?: string;
  extensions: string[];
  applied?: boolean;
  message?: string;
};

export type PostgresSummary = {
  phase?: string;
  instances: number;
  readyInstances: number;
  primary?: string;
  version?: string;
  storage?: string;
  cpu?: string;
  memory?: string;
};

const roleOfPod = (pod: DatastorePod): PgInstance['role'] => {
  const role =
    pod.metadata.labels?.['cnpg.io/instanceRole'] ?? pod.metadata.labels?.role;
  return role === 'primary' || role === 'replica' ? role : 'unknown';
};

const roleRank = (role: PgInstance['role']): number =>
  role === 'primary' ? 0 : 1;

export const pgInstances = (
  cluster: CnpgCluster | undefined,
  pods: DatastorePod[],
): PgInstance[] => {
  const healthByName = new Map<string, string>();
  Object.entries(cluster?.status?.instancesStatus ?? {}).forEach(
    ([health, names]) => names.forEach(n => healthByName.set(n, health)),
  );
  return pods
    .map(pod => ({
      name: pod.metadata.name,
      role:
        cluster?.status?.currentPrimary === pod.metadata.name
          ? ('primary' as const)
          : roleOfPod(pod),
      health: healthByName.get(pod.metadata.name) ?? 'unknown',
      node: pod.spec?.nodeName,
      podIp: pod.status?.podIP,
      phase: pod.status?.phase,
      ready:
        (pod.status?.containerStatuses ?? []).every(c => c.ready) &&
        pod.status?.phase === 'Running',
      restarts: (pod.status?.containerStatuses ?? []).reduce(
        (s, c) => s + (c.restartCount ?? 0),
        0,
      ),
    }))
    .sort(
      (a, b) =>
        roleRank(a.role) - roleRank(b.role) || a.name.localeCompare(b.name),
    );
};

/** CR относится к кластеру name (у Database/DatabaseRole поле spec.cluster.name обязательно). */
const ofCluster = (
  name: string,
  cr: { spec?: { cluster?: { name?: string }; ensure?: string } },
) => cr.spec?.cluster?.name === name && cr.spec?.ensure !== 'absent';

/**
 * Пользователи кластера name: владелец первой базы и суперпользователь — из Cluster,
 * остальные — CR DatabaseRole этого кластера (и spec.managed.roles, если кто-то описал
 * роли так). CR с именем уже известного пользователя уточняет его права.
 */
export const pgUsers = (
  name: string,
  cluster: CnpgCluster | undefined,
  roles: CnpgDatabaseRole[] = [],
): PgUser[] => {
  const users: PgUser[] = [];
  const owner = cluster?.spec?.bootstrap?.initdb?.owner;
  if (owner) {
    users.push({
      name: owner,
      source: 'owner',
      login: true,
      superuser: false,
      createdb: false,
      secret: `${name}-app`,
    });
  }
  if (cluster?.spec?.enableSuperuserAccess) {
    users.push({
      name: 'postgres',
      source: 'superuser',
      login: true,
      superuser: true,
      createdb: true,
      secret: `${name}-superuser`,
    });
  }
  const statusByRole = new Map<string, string>();
  Object.entries(cluster?.status?.managedRolesStatus ?? {}).forEach(
    ([status, names]) =>
      (names ?? []).forEach(n => statusByRole.set(n, status)),
  );
  for (const r of cluster?.spec?.managed?.roles ?? []) {
    if (r.ensure === 'absent') continue;
    users.push({
      name: r.name,
      source: 'managed',
      login: Boolean(r.login),
      superuser: Boolean(r.superuser),
      createdb: Boolean(r.createdb),
      secret: r.passwordSecret?.name,
      status: statusByRole.get(r.name),
    });
  }
  const declared = roles
    .filter(r => ofCluster(name, r))
    .map(
      (r): PgUser => ({
        name: r.spec?.name ?? r.metadata.name,
        source: 'role',
        resource: r.metadata.name,
        login: r.spec?.login ?? true,
        superuser: Boolean(r.spec?.superuser),
        createdb: Boolean(r.spec?.createdb),
        comment: r.spec?.comment || undefined,
        secret: r.spec?.passwordSecret?.name,
        applied: r.status?.applied,
        message: r.status?.message || undefined,
      }),
    )
    .sort((a, b) => a.name.localeCompare(b.name));
  return [
    ...users.filter(u => !declared.some(d => d.name === u.name)),
    ...declared,
  ];
};

/** Базы кластера name: первая — из initdb Cluster, остальные — CR Database этого кластера. */
export const pgDatabases = (
  name: string,
  cluster: CnpgCluster | undefined,
  databases: CnpgDatabase[],
): PgDatabase[] => {
  const result: PgDatabase[] = [];
  const initdb = cluster?.spec?.bootstrap?.initdb;
  if (initdb?.database) {
    result.push({
      name: initdb.database,
      owner: initdb.owner,
      source: 'bootstrap',
      extensions: [],
    });
  }
  const declared = databases
    .filter(db => ofCluster(name, db))
    .map(
      (db): PgDatabase => ({
        name: db.spec?.name ?? db.metadata.name,
        owner: db.spec?.owner,
        source: 'database',
        resource: db.metadata.name,
        extensions: (db.spec?.extensions ?? [])
          .filter(e => e.ensure !== 'absent')
          .map(e => e.name),
        applied: db.status?.applied,
        message: db.status?.message || undefined,
      }),
    )
    .sort((a, b) => a.name.localeCompare(b.name));
  return [
    ...result.filter(r => !declared.some(d => d.name === r.name)),
    ...declared,
  ];
};

/**
 * Базы, которые есть в PostgreSQL (по метрикам), но не описаны в IaC — заведены руками
 * вопреки правилу «только через IaC»; postgres и template* метрика уже не отдаёт.
 */
export const pgUnmanagedDatabases = (
  declared: PgDatabase[],
  actual: Iterable<string>,
): string[] =>
  [...actual]
    .filter(
      n =>
        !declared.some(d => d.name === n) &&
        n !== 'postgres' &&
        !n.startsWith('template'),
    )
    .sort();

export const pgSummary = (
  cluster: CnpgCluster | undefined,
): PostgresSummary => {
  const image = cluster?.status?.image ?? cluster?.spec?.imageName ?? '';
  const version = image.includes(':')
    ? image.slice(image.lastIndexOf(':') + 1)
    : undefined;
  return {
    phase: cluster?.status?.phase,
    instances: cluster?.status?.instances ?? cluster?.spec?.instances ?? 0,
    readyInstances: cluster?.status?.readyInstances ?? 0,
    primary: cluster?.status?.currentPrimary,
    version,
    storage: cluster?.spec?.storage?.size,
    cpu:
      cluster?.spec?.resources?.limits?.cpu ??
      cluster?.spec?.resources?.requests?.cpu,
    memory:
      cluster?.spec?.resources?.limits?.memory ??
      cluster?.spec?.resources?.requests?.memory,
  };
};

/* ------------------------------- Kafka (Strimzi) ------------------------------- */

export type StrimziKafka = {
  metadata: { name: string };
  spec?: { kafka?: { version?: string } };
  status?: {
    kafkaVersion?: string;
    kafkaMetadataVersion?: string;
    clusterId?: string;
    conditions?: Condition[];
    listeners?: { name: string; bootstrapServers?: string }[];
    kafkaNodePools?: { name: string }[];
  };
};

export type StrimziNodePool = {
  metadata: { name: string; labels?: Record<string, string> };
  spec?: {
    roles?: string[];
    replicas?: number;
    storage?: { type?: string; size?: string; volumes?: { size?: string }[] };
    resources?: {
      requests?: Record<string, string>;
      limits?: Record<string, string>;
    };
  };
  status?: { nodeIds?: number[]; replicas?: number; roles?: string[] };
};

export type StrimziTopic = {
  metadata: { name: string; labels?: Record<string, string> };
  spec?: {
    topicName?: string;
    partitions?: number;
    replicas?: number;
    config?: Record<string, unknown>;
  };
  status?: { topicName?: string; conditions?: Condition[] };
};

export type KafkaNode = {
  name: string;
  pool?: string;
  roles: string[];
  nodeId?: number;
  node?: string;
  podIp?: string;
  phase?: string;
  ready: boolean;
  restarts: number;
};

export type KafkaPool = {
  name: string;
  roles: string[];
  replicas: number;
  nodeIds: number[];
  storage?: string;
  cpu?: string;
  memory?: string;
};

export type KafkaTopic = {
  name: string;
  partitions?: number;
  replicas?: number;
  /** true — ресурс KafkaTopic; false — виден только по метрикам. */
  managed: boolean;
  ready?: boolean;
  retention?: string;
  cleanupPolicy?: string;
};

export type KafkaSummary = {
  ready: boolean;
  version?: string;
  metadataVersion?: string;
  bootstrap: { name: string; servers: string }[];
};

const isKafkaNodePod = (pod: DatastorePod) =>
  pod.metadata.labels?.['strimzi.io/kind'] === 'Kafka' &&
  Boolean(pod.metadata.labels?.['strimzi.io/pool-name']);

export const kafkaNodes = (pods: DatastorePod[]): KafkaNode[] =>
  pods
    .filter(isKafkaNodePod)
    .map(pod => {
      const l = pod.metadata.labels ?? {};
      const roles = [
        ...(l['strimzi.io/broker-role'] === 'true' ? ['broker'] : []),
        ...(l['strimzi.io/controller-role'] === 'true' ? ['controller'] : []),
      ];
      const idMatch = /-(\d+)$/.exec(pod.metadata.name);
      return {
        name: pod.metadata.name,
        pool: l['strimzi.io/pool-name'],
        roles,
        nodeId: idMatch ? Number(idMatch[1]) : undefined,
        node: pod.spec?.nodeName,
        podIp: pod.status?.podIP,
        phase: pod.status?.phase,
        ready:
          (pod.status?.containerStatuses ?? []).every(c => c.ready) &&
          pod.status?.phase === 'Running',
        restarts: (pod.status?.containerStatuses ?? []).reduce(
          (s, c) => s + (c.restartCount ?? 0),
          0,
        ),
      };
    })
    .sort(
      (a, b) =>
        (a.nodeId ?? 0) - (b.nodeId ?? 0) || a.name.localeCompare(b.name),
    );

export const kafkaPools = (pools: StrimziNodePool[]): KafkaPool[] =>
  pools
    .map(p => ({
      name: p.metadata.name,
      roles: p.status?.roles ?? p.spec?.roles ?? [],
      replicas: p.status?.replicas ?? p.spec?.replicas ?? 0,
      nodeIds: p.status?.nodeIds ?? [],
      storage:
        p.spec?.storage?.size ??
        p.spec?.storage?.volumes
          ?.map(v => v.size)
          .filter(Boolean)
          .join(' + '),
      cpu: p.spec?.resources?.limits?.cpu ?? p.spec?.resources?.requests?.cpu,
      memory:
        p.spec?.resources?.limits?.memory ??
        p.spec?.resources?.requests?.memory,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

const humanMs = (raw: unknown): string | undefined => {
  const ms = Number(raw);
  if (!Number.isFinite(ms) || ms < 0)
    return raw === undefined ? undefined : String(raw);
  if (ms === -1) return '∞';
  const days = ms / 86_400_000;
  if (days >= 1) return `${Math.round(days * 10) / 10} д`;
  const hours = ms / 3_600_000;
  if (hours >= 1) return `${Math.round(hours * 10) / 10} ч`;
  return `${Math.round(ms / 60_000)} мин`;
};

/** Топики: ресурсы KafkaTopic плюс топики, известные только по метрикам (partitions из Prometheus). */
export const kafkaTopics = (
  topics: StrimziTopic[],
  metricTopics: { topic: string; partitions: number }[] = [],
): KafkaTopic[] => {
  const result: KafkaTopic[] = topics.map(t => ({
    name: t.status?.topicName ?? t.spec?.topicName ?? t.metadata.name,
    partitions: t.spec?.partitions,
    replicas: t.spec?.replicas,
    managed: true,
    ready: t.status?.conditions?.some(
      c => c.type === 'Ready' && c.status === 'True',
    ),
    retention: humanMs(t.spec?.config?.['retention.ms']),
    cleanupPolicy: t.spec?.config?.['cleanup.policy'] as string | undefined,
  }));
  for (const m of metricTopics) {
    if (m.topic.startsWith('__') || result.some(r => r.name === m.topic))
      continue;
    result.push({ name: m.topic, partitions: m.partitions, managed: false });
  }
  return result.sort((a, b) => a.name.localeCompare(b.name));
};

export const kafkaSummary = (
  kafka: StrimziKafka | undefined,
): KafkaSummary => ({
  ready:
    kafka?.status?.conditions?.some(
      c => c.type === 'Ready' && c.status === 'True',
    ) ?? false,
  version: kafka?.status?.kafkaVersion ?? kafka?.spec?.kafka?.version,
  metadataVersion: kafka?.status?.kafkaMetadataVersion,
  bootstrap: (kafka?.status?.listeners ?? [])
    .filter(l => l.bootstrapServers)
    .map(l => ({ name: l.name, servers: l.bootstrapServers as string })),
});
